<!-- src/App.svelte -->
<script>
  import Logo from './LogoComponent.svelte';
  import IconButton from './ButtonComponent.svelte';
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import { getApiBase } from './apiBase.js';
  import OutstandingAgreementNotice from './OutstandingAgreementNotice.svelte';
  import { clearInboxRecovery } from './inbox/browserRecovery.js';

  let isUserTeacher = null;
  let errorMessage = '';
  let isUserSuper = null;
  let isLoading = true; // Add loading state
  let currentUsername = null;
  let isHerkulezas = false;

  function base64UrlDecode(input) {
    // JWT uses base64url: replace URL-safe chars and pad.
    const normalized = String(input).replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return atob(padded);
  }

  function getUsernameFromJwt(token) {
    try {
      const parts = String(token || '').split('.');
      if (parts.length < 2) return null;
      const payloadJson = base64UrlDecode(parts[1]);
      const payload = JSON.parse(payloadJson);
      return (
        payload?.username ||
        payload?.user_name ||
        payload?.preferred_username ||
        payload?.user?.username ||
        null
      );
    } catch {
      return null;
    }
  }

  function logout() {
    // Clear JWT token from localStorage or sessionStorage
    localStorage.removeItem('jwtToken'); // assuming your JWT is stored in localStorage
    // Failed best-effort metadata cleanup must never prevent authentication removal.
    try { clearInboxRecovery(); } catch { /* Server-owned commands remain recoverable after login. */ }
    window.dispatchEvent(new Event('tutory:logout'));
    
    // Optionally clear other user-related data from storage
    // localStorage.removeItem('userInfo');

    console.log('User logged out');

    // Redirect the user to the login page
    push('/about'); // Use the appropriate path to your login page
  }
  
  // Create reactive button arrays based on user type
  $: studentButtons = [
    { icon: 'fa-solid fa-user-plus', label: 'REGISTRUOTIS Į PAMOKĄ', link:'/registration-for-class' },
    { icon: 'fa-solid fa-calendar-days', label: 'TVARKARAŠTIS', link:'/schedule' },
    { icon: 'fa-solid fa-file-invoice-dollar', label: 'SĄSKAITOS', link:'/invoice' },
    { icon: 'fa-solid fa-circle-info', label: 'BENDRA INFORMACIJA', link:'/bendra-informacija' },
    { icon: 'fa-solid fa-user-circle', label: 'MANO PROFILIS', link:'/settingsAndTimeslots' }
  ];

  $: teacherButtons = [
    { icon: 'fa-solid fa-chart-line', label: 'STATISTIKA', link:'/teacher-performance' },
    { icon: 'fa-solid fa-calendar-days', label: 'TVARKARAŠTIS', link:'/schedule' },
    { icon: 'fa-solid fa-file-invoice-dollar', label: 'SĄSKAITOS', link:'/invoice' },
    { icon: 'fa-solid fa-user-circle', label: 'MANO PROFILIS', link:'/settingsAndTimeslots' }
  ];

  // Super users: keep the "main 4" together, and show dashboard buttons
  // as a separate section below them.
  $: superUserMainButtons = [
    { icon: 'fa-solid fa-envelope', label: 'COMMUNICATION & SCHEDULING', link:'/adminSearch' },
    { icon: 'fa-solid fa-users', label: 'USER MANAGEMENT', link:'/admin' },
    { icon: 'fa-solid fa-user-tie', label: 'TEACHER MONITORING', link:'/teacherSurveilance' },
    { icon: 'fa-solid fa-user-circle', label: 'MANO PROFILIS', link:'/settingsAndTimeslots' }
  ];

  // Dashboard buttons should be visible to all super users.
  $: superUserDashboardButtons = [
    { icon: 'fa-solid fa-user-graduate', label: 'STUDENTS', link:'/admin/students' },
    { icon: 'fa-solid fa-user-tie', label: 'TEACHERS', link:'/admin/teachers' },
    { icon: 'fa-solid fa-toolbox', label: 'ADMIN', link:'/admin/all' },
  ];

  // Determine which buttons to show based on user type
  $: mainButtons = isUserSuper ? superUserMainButtons : (isUserTeacher ? teacherButtons : studentButtons);
  $: dashboardButtons = isUserSuper ? superUserDashboardButtons : [];

  // This function fetches the teacher status from the backend API
  async function checkIfUserIsTeacher() {
    try {
        // Retrieve JWT token from localStorage/sessionStorage/cookie (depending on how you store it)
        const token = localStorage.getItem('jwtToken'); 
        if (!token) {
            throw new Error('JWT token not found. Please log in.');
        }

        currentUsername = getUsernameFromJwt(token);
        isHerkulezas = (String(currentUsername || '').toLowerCase() === 'herkulezas');

        const apiBase = getApiBase();
        const response = await fetch(`${apiBase}/studentplayground/isUserTeacher/`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            // Handle error response from the server
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to check teacher status');
        }

        const data = await response.json();
        isUserTeacher = data.isUserTeacher;
        const response2 = await fetch(`${apiBase}/studentplayground/isSuperUser/`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response2.ok) {
            // Handle error response from the server
            const errorData = await response2.json();
            throw new Error(errorData.error || 'Failed to check teacher status');
        }
        
        const data2 = await response2.json();
        isUserSuper = data2.isUserSuper;
    } catch (error) {
        errorMessage = error.message;
    } finally {
        isLoading = false; // Set loading to false when API calls complete
    }
    
  }

    // Check if user is authenticated
  function checkAuthentication() {
    const token = localStorage.getItem('jwtToken');
    const expiry = localStorage.getItem('jwtExpiry');
    
    // Check if token exists and is not expired
    if (!token || !expiry || Date.now() >= parseInt(expiry)) {
      // Store current URL params to preserve redirect intent
      const currentParams = window.location.search;
      push('/about' + currentParams);
      return false;
    }
    
    return true;
  }

    // Fetch teacher status on component mount
  onMount(async () => {
    // First check if user is authenticated
    if (!checkAuthentication()) {
      return; // Exit early if not authenticated
    }
    
    // Check for redirect parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTo = urlParams.get('redirect');
    
    if (redirectTo === 'invoice') {
      // Set a flag to indicate user was redirected to invoice page
      sessionStorage.setItem('redirectedToInvoice', 'true');
      push('/invoice');
      return;
    }
    
    await checkIfUserIsTeacher(); // Await this if it's asynchronous
    
    // Update last activity timestamp (non-blocking)
    updateLastActivity();
  
  });

  // Function to update user's last activity timestamp
  async function updateLastActivity() {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) return; // Skip if no token
      
      // Make non-blocking call to update last activity
      const apiBase = getApiBase();
      fetch(`${apiBase}/studentplayground/api/update-last-activity/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).then(response => {
        if (response.ok) {
          console.log('Last activity updated successfully');
        }
      }).catch(error => {
        // Silently handle errors to avoid disrupting user experience
        console.log('Last activity update failed (non-critical):', error);
      });
    } catch (error) {
      // Silently handle errors
      console.log('Last activity update error (non-critical):', error);
    }
  }

</script>
<div class="greeting-page">
  <div class="content">
    <img src="/static/images/Tutory Logo Black 1.png" alt="TUTORY Logo" class="logo">
    {#if !isLoading && isUserSuper === false}
      <OutstandingAgreementNotice />
    {/if}
    <section class="button-group">
      {#if isLoading}
        <!-- Show loading state or nothing while determining user type -->
        <div class="loading-placeholder"></div>
      {:else}
        {#each mainButtons as button}
          <IconButton icon={button.icon} label={button.label} link={button.link}/>
        {/each}
      {/if}
    </section>
  </div>

  {#if !isLoading && dashboardButtons.length}
    <section class="dashboard-buttons" aria-label="Dashboard buttons">
      {#each dashboardButtons as button}
        <IconButton icon={button.icon} label={button.label} link={button.link}/>
      {/each}
    </section>
  {/if}

  <!-- Support Buttons -->
  <div class="support-buttons">
    <a href="viber://chat?number=%2B37068713647" class="viber-button" aria-label="Chat with us on Viber">
      <i class="fab fa-viber"></i>
      <span>Susisiekite su mumis per Viber</span>
    </a>

    <a href="https://wa.me/message/LEPGDN5U5TE3B1" class="whatsapp-button" aria-label="Chat with us on WhatsApp">
      <i class="fab fa-whatsapp"></i>
      <span>Susisiekite su mumis per WhatsApp</span>
    </a>
  </div>

  <div class="logout-container">
    <span class="logout-text" on:click={logout} on:keydown={(e) => e.key === 'Enter' && logout()} tabindex="0" role="button" aria-label="Log out">Atsijungti</span>
  </div>
</div>

<style>
  .greeting-page {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    width: 100%;
    max-width: 450px;
    margin: 0 auto;
    padding: 12px 0 28px;
    box-sizing: border-box;
  }

  .dashboard-buttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    align-items: center;
    margin: 0;
  }

  .support-buttons {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin: 0;
    width: 100%;
    align-items: center;
  }

  /* If dashboard buttons are present, avoid "double gap" before contact buttons. */
  .dashboard-buttons + .support-buttons {
    margin-top: 0;
  }

  .viber-button, .whatsapp-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 12px 20px;
    color: white;
    text-decoration: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 500;
    transition: background-color 0.3s ease, transform 0.2s ease;
    width: 100%;
    box-sizing: border-box;
    cursor: pointer;
  }

  .viber-button {
    background-color: #7360f2;
  }

  .viber-button:hover {
    background-color: #5d4fd1;
    transform: translateY(-2px);
  }

  .viber-button:focus {
    outline: 2px solid #7360f2;
    outline-offset: 2px;
  }

  .whatsapp-button {
    background-color: #25d366;
  }

  .whatsapp-button:hover {
    background-color: #22c55e;
    transform: translateY(-2px);
  }

  .whatsapp-button:focus {
    outline: 2px solid #25d366;
    outline-offset: 2px;
  }

  .viber-button i, .whatsapp-button i {
    font-size: 20px;
  }

  .logout-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0;
    width: 100%;
  }

  .logout-text {
    font-size: 16px;
    color: #666;
    cursor: pointer;
    transition: color 0.3s ease;
    text-decoration: none;
    border: none;
    background: none;
    padding: 12px 20px;
    border-radius: 8px;
    transition: background-color 0.3s ease, color 0.3s ease;
    display: block;
    text-align: center;
    width: 100%;
    box-sizing: border-box;
  }

  .logout-text:hover {
    color: #14695b;
    background-color: rgba(20, 105, 91, 0.1);
  }

  .logout-text:focus {
    outline: 2px solid #14695b;
    outline-offset: 2px;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 0;
    gap: 28px;
    box-sizing: border-box;
  }

  .logo {
    display: block;
    width: 250px;
    max-width: 100%;
    height: auto;
  }

  .button-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .loading-placeholder {
    height: 200px; /* Adjust height to match approximate button height */
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    .greeting-page {
      gap: 16px;
      padding-top: 4px;
    }

    .content {
      gap: 20px;
    }
  }
</style>
